import { Box } from '@mui/material'
import { CarrierType } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { useOrderBreadcrumbsV2 } from '@planning/hooks/useOrderBreadcrumbsV2'
import { Breadcrumbs, Loader, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { StepperForm } from './components/StepperForm/StepperForm'
import { StepperFormViewStore } from './components/StepperForm/StepperFormViewStore'
import { OrderReviewStore } from './components/StepperForm/stores/OrderReviewStore'
import { HandleOrderPageLocationState } from './HandleOrderPage'
import {
  CreateOrderPageViewStore,
  isoCodeMappingFieldRenderFnType,
} from './stores/CreateOrderPageViewStore'
import { OrderFormStore, OrderFormStoreType } from './V2/stores/OrderFormStore'
import { CreateOrderPageHeader } from './V2/templates/CreateOrderPageHeader'

const DEFAULT_RETURN_PATH = PATHS.visits.visits.url

type Props = {
  isoCodeMappingFieldRenderFn: isoCodeMappingFieldRenderFnType
}

export const CreateOrderPage: FC<Props> = observer(({ isoCodeMappingFieldRenderFn }) => {
  const { appViewStore, containerItemStore } = usePlanningStore()
  const theme = useMinimalsTheme()
  const navigate = useNavigate()

  const { getRouteBreadcrumbs, isVesselVisitRoute } = useOrderBreadcrumbsV2()
  const { state } = useLocation() as { state: HandleOrderPageLocationState }
  const { visitId, direction } = useParams()
  const [searchParams] = useSearchParams()
  const containerId = searchParams.get('containerId')

  const formStore = useMemo(() => new OrderFormStore(), [])
  const stepperFormViewStore = useMemo(() => new StepperFormViewStore(), [])
  const orderReviewStore = useMemo(() => new OrderReviewStore(), [])

  const createOrderPageViewStore = useMemo(
    () =>
      new CreateOrderPageViewStore(
        formStore,
        stepperFormViewStore,
        orderReviewStore,
        containerItemStore,
        isoCodeMappingFieldRenderFn,
        direction === 'restow',
        visitId ? Number(visitId) : undefined,
        containerId ? Number(containerId) : undefined,
      ),
    [
      formStore,
      stepperFormViewStore,
      orderReviewStore,
      containerItemStore,
      isoCodeMappingFieldRenderFn,
      direction,
      visitId,
      containerId,
    ],
  )

  const onBack = () => {
    let returnPath = DEFAULT_RETURN_PATH

    if (state?.returnPath) {
      returnPath = state.returnPath

      if (state.returnPathAdditionalParams) {
        if (
          state.returnPathAdditionalParams.containerNumber &&
          createOrderPageViewStore.container?.number
        ) {
          returnPath += `/${createOrderPageViewStore.container.number}`
        }
      }
    }
    navigate(returnPath)
  }

  const initialValues: Partial<OrderFormStoreType> = useMemo(() => {
    if (!visitId || !direction) return {}

    const carrierType = isVesselVisitRoute ? CarrierType.Vessel : CarrierType.Train
    const carrierVisitId = Number(visitId)

    const transport = {
      carrierType,
      carrierVisitId,
    }

    const cargoDetails = {
      cargoDetails: {
        isEmpty: true,
      },
      customs: [],
      oog: {},
      seals: {
        hasSeals: false,
      },
    }

    const initialValues: Partial<OrderFormStoreType> = {}

    if (direction === 'discharge' || direction === 'restow') {
      initialValues.inboundCargoDetails = {
        ...cargoDetails,
        transport,
      }
    }

    if (direction === 'load' || direction === 'restow') {
      initialValues.outboundCargoDetails = {
        ...cargoDetails,
        transport,
      }
    }

    return initialValues
  }, [direction, isVesselVisitRoute, visitId])

  return (
    <>
      <Loader show={appViewStore.isLoading} />
      <Breadcrumbs items={getRouteBreadcrumbs()} />
      <CreateOrderPageHeader createOrderPageViewStore={createOrderPageViewStore} onBack={onBack} />
      <Box
        paddingX={theme.customSpacing.l}
        paddingY={theme.customSpacing.m}
        gap={theme.customSpacing.m}
        display={'flex'}
        flexDirection={'column'}
        sx={{ background: theme.palette.background.neutral }}
        minHeight={'calc(100vh - 114px)'}
      >
        <StepperForm
          createOrderPageViewStore={createOrderPageViewStore}
          initialValues={initialValues}
        />
      </Box>
    </>
  )
})
